import React from 'react'
import styled from 'styled-components'

const Card = styled.div`
    width: 650px;
    border-radius: 16px;
    box-shadow: rgba(23, 92, 230, 0.15) 0px 4px 24px;
    padding: 18px 20px;
    justify-content: space-between;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 14px;
    transition: all 0.3s ease-in-out;
    border: 0.1px solid #306EE8;
    
    &:hover {
        box-shadow: rgba(23, 92, 230, 0.3) 0px 8px 32px;
        transform: translateY(-8px);
    }
    
    @media only screen and (max-width: 768px){
        padding: 14px 16px;
        gap: 10px;
        width: 100%;
        max-width: 400px;
    }
`

const Top = styled.div`
    width: 100%;
    display: flex;
    gap: 14px;
    align-items: flex-start;
`

const LogoContainer = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 12px;
    padding: 6px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    flex-shrink: 0;
`

const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    @media only screen and (max-width: 768px){
        width: 40px;
        height: 40px;
    }
`

const Body = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`

const Name = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary + 99};
    @media only screen and (max-width: 768px){
        font-size: 16px;
    }
`

const DegreeTypeBadge = styled.span`
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    background: ${props => {
        switch (props.type) {
            case 'Masters': return 'linear-gradient(135deg, #854CE6 0%, #9D5CE8 100%)';
            case 'Bachelors': return 'linear-gradient(135deg, #0073E6 0%, #0056D2 100%)';
            case 'High School': return 'linear-gradient(135deg, #00C853 0%, #00EA64 100%)';
            case 'Secondary School': return 'linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)';
            default: return 'linear-gradient(135deg, #854CE6 0%, #9D5CE8 100%)';
        }
    }};
    color: white;
    @media only screen and (max-width: 768px){
        font-size: 10px;
        padding: 3px 10px;
    }
`

const Degree = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.text_secondary + 99};
    @media only screen and (max-width: 768px){
        font-size: 13px;
    }
`

const Date = styled.div`
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_secondary + 80};
    @media only screen and (max-width: 768px){
        font-size: 11px;
    }
`

const GradeHighlight = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: linear-gradient(135deg, ${({ theme }) => theme.primary + 20}, ${({ theme }) => theme.primary + 10});
    border: 1px solid ${({ theme }) => theme.primary + 30};
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.primary};
    width: fit-content;
    
    @media only screen and (max-width: 768px){
        font-size: 12px;
        padding: 5px 12px;
    }
`

const CourseworkSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const CourseworkLabel = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_primary};
`

const CourseworkBadges = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`

const CourseworkBadge = styled.span`
    background: ${({ theme }) => theme.primary + 20};
    color: ${({ theme }) => theme.primary};
    padding: 4px 12px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.primary + 30};
    font-size: 12px;
    font-weight: 500;
    transition: all 0.3s ease-in-out;
    
    &:hover {
        background: ${({ theme }) => theme.primary + 30};
        transform: translateY(-2px);
    }
    
    @media only screen and (max-width: 768px){
        font-size: 11px;
        padding: 3px 10px;
    }
`

const EducationCard = ({ education }) => {
    return (
        <Card>
            <Top>
                <LogoContainer>
                    <Image src={education.img} alt={education.school} />
                </LogoContainer>
                <Body>
                    <HeaderRow>
                        <Name>{education.school}</Name>
                        {education.degreeType && (
                            <DegreeTypeBadge type={education.degreeType}>
                                {education.degreeType === 'Masters' && '🎓 Masters'}
                                {education.degreeType === 'Bachelors' && '🎓 Bachelors'}
                                {education.degreeType === 'High School' && '📚 High School'}
                                {education.degreeType === 'Secondary School' && '📚 Secondary'}
                            </DegreeTypeBadge>
                        )}
                    </HeaderRow>
                    <Degree>{education.degree}</Degree>
                    <Date>{education.date}</Date>
                </Body>
            </Top>

            <GradeHighlight>
                ⭐ {education.grade}
            </GradeHighlight>

            {education.coursework && education.coursework.length > 0 && (
                <CourseworkSection>
                    <CourseworkLabel>Key Courses:</CourseworkLabel>
                    <CourseworkBadges>
                        {education.coursework.map((course, index) => (
                            <CourseworkBadge key={index}>{course}</CourseworkBadge>
                        ))}
                    </CourseworkBadges>
                </CourseworkSection>
            )}
        </Card>
    )
}

export default EducationCard